import { useState, useEffect, useRef } from 'react'
import MapboxDraw from '@mapbox/mapbox-gl-draw'

import BtnGrid from '@App/components/BtnGrid/BtnGrid'
import IconBtnHor from '@App/components/IconBtnHor/IconBtnHor'

import './draw-mode-toggles.styl'

const hasControl = (map, ControlType) => {
  return map._controls.some(control => control instanceof ControlType)
}

const mapDrawMode = (mode) => {
  const modeMap = {
    point: 'draw_point',
    polygon: 'draw_polygon',
    line: 'draw_line_string',
  }
  return modeMap[mode] || mode
}

const drawStyles = [
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': ['case', ['==', ['get', 'active'], 'true'], '#fbb03b', '#3bb2d0'],
      'fill-opacity': 0.1,
    },
  },
  {
    id: 'gl-draw-lines',
    type: 'line',
    filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ['case', ['==', ['get', 'active'], 'true'], '#fbb03b', '#3bb2d0'],
      'line-dasharray': [0.2, 2],
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-point',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['!=', 'meta', 'vertex']],
    paint: {
      'circle-radius': 6,
      'circle-color': ['case', ['==', ['get', 'active'], 'true'], '#fbb03b', '#3bb2d0'],
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2,
    },
  },
  {
    id: 'gl-draw-vertex',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#3bb2d0',
    },
  },
]

function DrawModeToggles(props) {
  const { map, drawModes, onDraw } = props
  const [activeDrawMode, setActiveDrawMode] = useState(null)
  const [hasDrawnFeature, setHasDrawnFeature] = useState(false)
  const drawRef = useRef(null)
  const activeDrawModeRef = useRef(activeDrawMode)
  const lastDrawnFeatureId = useRef(null)

  activeDrawModeRef.current = activeDrawMode

  useEffect(() => {
    return () => {
      lastDrawnFeatureId.current = null
      if (map && drawRef.current && hasControl(map, MapboxDraw)) {
        drawRef.current.deleteAll()
      }
    }
  }, [map])

  useEffect(() => {
    if (drawModes) {
      setActiveDrawMode(drawModes && drawModes[0])
    } else {
      setActiveDrawMode(null)
    }
  }, [drawModes])

  useEffect(() => {
    if (!map) return

    if (!drawRef.current) {
      drawRef.current = new MapboxDraw({
        displayControlsDefault: false,
        styles: drawStyles,
      })
    }

    if (hasControl(map, MapboxDraw)) {
      map.removeControl(drawRef.current)
    }

    map.addControl(drawRef.current)

    const handleDrawCreate = (e) => {
      if (!activeDrawModeRef.current) return

      if (lastDrawnFeatureId.current) {
        drawRef.current.delete(lastDrawnFeatureId.current)
      }

      const newFeature = e.features[0]
      lastDrawnFeatureId.current = newFeature.id
      setHasDrawnFeature(true)

      if (onDraw) {
        onDraw(newFeature)
      }

      setTimeout(() => {
        if (drawRef.current && newFeature.id) {
          if (newFeature.geometry.type === 'Point') {
            drawRef.current.changeMode('draw_point')
          } else {
            drawRef.current.changeMode('direct_select', { featureId: newFeature.id })
          }
        }
      })
    }

    const handleDrawUpdate = (e) => {
      if (!onDraw || !e?.features?.length) return
      const updatedFeature = e.features[0]
      if (updatedFeature?.id) {
        lastDrawnFeatureId.current = updatedFeature.id
      }
      onDraw(updatedFeature)
    }

    map.on('draw.create', handleDrawCreate)
    map.on('draw.update', handleDrawUpdate)

    return () => {
      map.off('draw.create', handleDrawCreate)
      map.off('draw.update', handleDrawUpdate)
      if (map && drawRef.current && hasControl(map, MapboxDraw)) {
        map.removeControl(drawRef.current)
      }
    }
  }, [map, onDraw])

  useEffect(() => {
    if (!map || !drawRef.current) return

    if (!activeDrawMode) {
      return
    }

    drawRef.current.deleteAll()
    lastDrawnFeatureId.current = null
    setHasDrawnFeature(false)

    const mappedMode = mapDrawMode(activeDrawMode)
    const validModes = ['draw_point', 'draw_line_string', 'draw_polygon', 'simple_select', 'direct_select']

    if (validModes.includes(mappedMode)) {
      drawRef.current.changeMode(mappedMode)
    } else {
      console.warn(`Invalid draw mode: ${activeDrawMode} (mapped to ${mappedMode}). Valid modes are: ${validModes.join(', ')}`)
    }
  }, [map, activeDrawMode])

  useEffect(() => {
    if (!map) return

    const canvas = map.getCanvas()
    const mappedMode = mapDrawMode(activeDrawMode)
    const isPointDraw = mappedMode === 'draw_point'
    if ((activeDrawMode && !hasDrawnFeature) || isPointDraw) {
      canvas.style.cursor = 'crosshair'
    } else {
      canvas.style.cursor = ''
    }

    return () => {
      canvas.style.cursor = ''
    }
  }, [map, activeDrawMode, hasDrawnFeature])

  return (
    <div className="draw-mode-toggles">
      {hasDrawnFeature
        ? (
            <p className="draw-mode-toggles__feature-drawn">
              A feature was drawn, you can still edit it on the map or
              {' '}
              <button
                type="button"
                className="draw-mode-toggles__clear-link"
                onClick={() => {
                  if (drawRef.current) {
                    drawRef.current.deleteAll()
                  }
                  lastDrawnFeatureId.current = null
                  setHasDrawnFeature(false)
                  if (onDraw) {
                    onDraw(null)
                  }
                  if (drawRef.current && activeDrawModeRef.current) {
                    const mappedMode = mapDrawMode(activeDrawModeRef.current)
                    const validModes = ['draw_point', 'draw_line_string', 'draw_polygon', 'simple_select', 'direct_select']
                    if (validModes.includes(mappedMode)) {
                      drawRef.current.changeMode(mappedMode)
                    }
                  }
                }}
              >
                delete
              </button>
              {' '}
              it.
            </p>
          )
        : (
            <>
              {(!drawModes || !drawModes.length) && (
                <p className="draw-mode-toggles__no-modes">
                  Select a data type to begin drawing.
                </p>
              )}
              {(drawModes && drawModes.length) && (
                <p className="draw-mode-toggles__start">
                  Draw data on the map by clicking on it.
                </p>
              )}
            </>
          )}
      <BtnGrid>
        {drawModes && drawModes.map((drawMode) => {
          return (
            <IconBtnHor
              key={drawMode}
              iconProps={{ icon: `/img/icons/draw/${drawMode}.svg`, size: '22px' }}
              inactive={activeDrawMode !== drawMode}
              onClick={() => {
                setActiveDrawMode(drawMode)
              }}
            >
              {drawMode.charAt(0).toUpperCase() + drawMode.slice(1)}
            </IconBtnHor>
          )
        })}
      </BtnGrid>
    </div>
  )
}

export default DrawModeToggles
